import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  TablePagination,
  TextField,
  Typography,
  Collapse,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import { useQuery } from "react-query";
import { getAllNotifications } from "../../../api/services/api";
import {
  StyledDrawerContent,
  StyledDrawerHeader,
} from "../../../components/DrawerTemplate";
import EmptyScreen from "../../../components/EmptyScreen";
import Loader from "../../../components/Loader";
import Table from "../../../components/TableComponent";
import { RoleGuard } from "../../../components/RBAC";
import { NotificationForm } from "./NotificationForm";

export const NotificationsManagement = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState<number>(10);
  const [page, setPage] = useState<number>(0);
  const [open, setOpen] = useState(false);

  const { data, isLoading } = useQuery(
    [
      "notifications-list",
      {
        search: search,
        limit: limit,
        offset: limit * page,
      },
    ],
    getAllNotifications
  );

  // Access the response structure: data = { data: [...array...], count: number }
  const responseData = (data as any)?.data;
  const notifications = Array.isArray(responseData) ? responseData : [];
  const totalCount = (data as any)?.count || 0;

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: any) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAdd = () => {
    toggleDrawer(true);
  };

  const toggleDrawer = (newOpen: any) => {
    setOpen(newOpen);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const toggleRowExpansion = (batchId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [batchId]: !prev[batchId],
    }));
  };

  const Columns = [
    {
      title: "",
      dataIndex: "batchId",
      key: "expand",
      render: (_: any, record: any) => (
        <IconButton
          size="small"
          onClick={() => toggleRowExpansion(record.batchId)}
          aria-label={expandedRows[record.batchId] ? "Collapse" : "Expand"}
        >
          <ExpandMoreIcon
            sx={{
              transform: expandedRows[record.batchId]
                ? "rotate(180deg)"
                : "rotate(0deg)",
              transition: "transform 0.3s",
            }}
          />
        </IconButton>
      ),
    },
    {
      title: "Batch ID",
      dataIndex: "batchId",
      key: "batchId",
      render: (batchId: string) => (
        <Typography
          variant="caption"
          sx={{
            fontFamily: "monospace",
            fontSize: "0.75rem",
            color: "text.secondary",
            maxWidth: "120px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            display: "block",
          }}
          title={batchId}
        >
          {batchId}
        </Typography>
      ),
    },
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (title: string) => (
        <Typography variant="body2" fontWeight={500}>
          {title}
        </Typography>
      ),
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
      render: (colData: any) => (
        <Typography
          variant="body2"
          sx={{
            maxWidth: "300px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {colData}
        </Typography>
      ),
    },
    {
      title: "Recipients",
      dataIndex: "totalRecipients",
      key: "totalRecipients",
      render: (count: number) => (
        <Chip
          label={`${count} recipient${count !== 1 ? "s" : ""}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      title: "Type",
      dataIndex: "recipientType",
      key: "recipientType",
      render: (colData: any) => (
        <Chip
          label={colData?.replace(/_/g, " ") || "N/A"}
          size="small"
          color="default"
          variant="outlined"
        />
      ),
    },
    {
      title: "Success",
      dataIndex: "successCount",
      key: "successCount",
      render: (count: number, record: any) => (
        <Chip
          label={`${count}/${record.totalRecipients}`}
          size="small"
          color="success"
          variant={count === record.totalRecipients ? "filled" : "outlined"}
        />
      ),
    },
    {
      title: "Failed",
      dataIndex: "failedCount",
      key: "failedCount",
      render: (count: number) =>
        count > 0 ? (
          <Chip label={count} size="small" color="error" variant="filled" />
        ) : (
          <Chip label="0" size="small" color="default" variant="outlined" />
        ),
    },
    {
      title: "Sent",
      dataIndex: "sentCount",
      key: "sentCount",
      render: (count: number) =>
        count > 0 ? (
          <Chip label={count} size="small" color="info" variant="outlined" />
        ) : null,
    },
    {
      title: "Sent Date",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (colData: any) => (
        <Typography variant="body2" color="text.secondary">
          {formatDate(colData)}
        </Typography>
      ),
    },
  ];

  // Custom row renderer to add expandable content
  const expandableRowRender = (record: any) => {
    if (!expandedRows[record.batchId]) return null;

    return (
      <Box sx={{ p: 3, bgcolor: "grey.50", borderRadius: 1 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Recipients Details ({record.totalRecipients})
          </Typography>
          <Box display="flex" gap={1}>
            <Chip
              label={`✓ ${record.successCount} Success`}
              size="small"
              color="success"
              variant="filled"
            />
            {record.failedCount > 0 && (
              <Chip
                label={`✗ ${record.failedCount} Failed`}
                size="small"
                color="error"
                variant="filled"
              />
            )}
            {record.sentCount > 0 && (
              <Chip
                label={`→ ${record.sentCount} Sent`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
        </Box>
        <MuiTable size="small" sx={{ bgcolor: "white", borderRadius: 1 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {record.recipients.map((recipient: any) => (
              <TableRow
                key={recipient.id}
                sx={{
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={500}>
                    {recipient.user?.fullName || "Unknown User"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {recipient.user?.email || "No email"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={recipient.user?.userType || recipient.entityType}
                    size="small"
                    color={
                      recipient.entityType === "PATIENT"
                        ? "primary"
                        : "secondary"
                    }
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={recipient.status}
                    size="small"
                    color={
                      recipient.status === "SUCCESS"
                        ? "success"
                        : recipient.status === "FAILED"
                        ? "error"
                        : "info"
                    }
                    variant="filled"
                    sx={{
                      fontWeight: 600,
                      minWidth: "80px",
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </MuiTable>
      </Box>
    );
  };

  return (
    <Box my={2}>
      <Box mb={2} display="flex" gap={2} alignItems="center">
        <Typography variant="h3">Notifications Management</Typography>
        <RoleGuard allowedRoles={["SUPER_ADMIN", "ADMIN"]}>
          <Button variant="contained" onClick={handleAdd}>
            + Send Notification
          </Button>
        </RoleGuard>
      </Box>
      <Box>
        <TextField
          label="Search notifications"
          sx={{ width: "450px", padding: "0px" }}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>
      <Box>
        {!notifications || notifications.length === 0 ? (
          <Box mt={4}>
            <EmptyScreen type="notifications" />
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
              mt={2}
            >
              {search
                ? "No notifications match your search"
                : "No notifications have been sent yet"}
            </Typography>
          </Box>
        ) : (
          <>
            {isLoading ? (
              <Loader />
            ) : (
              <Box mt={2}>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Showing {notifications.length || 0} of {totalCount || 0}{" "}
                  notification batch
                  {totalCount !== 1 ? "es" : ""}
                </Typography>
                <Box>
                  {notifications.map((record: any) => (
                    <Box key={record.batchId} mb={2}>
                      <Table
                        dataSource={[record]}
                        rowKey={"batchId"}
                        columns={Columns}
                      />
                      <Collapse
                        in={expandedRows[record.batchId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        {expandableRowRender(record)}
                      </Collapse>
                    </Box>
                  ))}
                </Box>
                <Box
                  sx={{
                    marginTop: "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <TablePagination
                    count={totalCount || 0}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={limit}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <Drawer anchor="right" open={open} onClose={() => toggleDrawer(false)}>
        <StyledDrawerHeader>
          <Typography>Send Notification</Typography>{" "}
          <Typography
            sx={{
              fontSize: "14px",
              fontFamily: "Inter_semibold",
              color: "black",
              opacity: "0.5",
              cursor: "pointer",
            }}
            onClick={() => toggleDrawer(false)}
          >
            Cancel
          </Typography>
        </StyledDrawerHeader>
        <StyledDrawerContent>
          <NotificationForm toggleDrawer={(e: any) => toggleDrawer(e)} />
        </StyledDrawerContent>
      </Drawer>
    </Box>
  );
};
